import React from 'react'
import {
    type FieldValues,
    FormProvider,
    type SubmitHandler,
    useForm,
    type UseFormProps,
    Form as RhfForm,
    type FormProps as RhfFormProps,
} from 'react-hook-form'
import { RhfDebug } from './RhfDebug'
import { RhfDevTool, type RhfDevToolProps } from './RhfDevTool'
import { Stack, type StackProps } from '@mui/material'

type AppFormProps<
    TFieldValues extends FieldValues = FieldValues,
    TTransformedValues extends FieldValues | undefined = undefined,
> = UseFormProps<TFieldValues, TTransformedValues> & {
    children: React.ReactNode
    onSubmit?: SubmitHandler<TFieldValues>
    debug?: boolean
    devTool?: boolean
    devToolProps?: RhfDevToolProps
} & Pick<StackProps, 'sx'>

export const AppForm = <FormValues extends FieldValues>({
    children,
    onSubmit,
    debug,
    devTool,
    devToolProps,
    sx,
    ...options
}: AppFormProps<FormValues>): JSX.Element => {
    const form = useForm<FormValues>(options)

    return (
        <FormProvider {...form}>
            <Stack
                sx={sx}
                component="form"
                noValidate={!options.shouldUseNativeValidation}
                onSubmit={onSubmit ? form.handleSubmit(onSubmit) : undefined}
            >
                {children}
                {debug && <RhfDebug />}
                {devTool && <RhfDevTool {...devToolProps} />}
            </Stack>
        </FormProvider>
    )
}

type AppFormV2Props<
    TFieldValues extends FieldValues = FieldValues,
    TTransformedValues extends FieldValues | undefined = undefined,
> = RhfFormProps<TFieldValues, TTransformedValues> &
    UseFormProps<TFieldValues, TTransformedValues> & {
        debug?: boolean
        devTool?: boolean
        devToolProps?: RhfDevToolProps
    }

/**
 * Uses the new React Hook Form `<Form>` component
 *
 * https://www.react-hook-form.com/api/useform/form/
 *
 * Note: This component is currently in BETA */
export const AppFormV2 = <
    TFieldValues extends FieldValues = FieldValues,
    TTransformedValues extends FieldValues | undefined = undefined,
>({
    debug,
    devTool,
    devToolProps,
    children,
    ...formProps
}: AppFormV2Props<TFieldValues, TTransformedValues>) => {
    const methods = useForm<TFieldValues, TTransformedValues>(formProps)

    return (
        <FormProvider {...methods}>
            <RhfForm control={methods.control} {...formProps}>
                {children}
                {debug && <RhfDebug />}
                {devTool && <RhfDevTool {...devToolProps} />}
            </RhfForm>
        </FormProvider>
    )
}

export { AppForm as Form }
export { AppFormV2 as FormV2 }
export type { AppFormProps as FormProps }
export type { AppFormV2Props as FormV2Props }
